import React from 'react';
import { StyleSheet, css } from 'aphrodite';
import { useNavigate } from 'react-router-dom';
import customerImage from '../../assets/home/customer-ordering.png';
import restaurantImage from '../../assets/home/restaurant-growth.png';
import riderImage from '../../assets/home/rider-delivery.png';
import { colors, shared } from '../../styles/theme';
import ArrowIcon from '../ArrowIcon';

const audiences = [
  {
    eyebrow: 'For Customers',
    title: 'Good food, made easy.',
    text: 'Discover local favourites and get reliable delivery right to your door.',
    action: 'Explore customer access',
    route: '/customers',
    image: customerImage,
    alt: 'A customer ordering from her phone',
  },
  {
    eyebrow: 'For Restaurants',
    title: 'More customers. Bigger tomorrows.',
    text: 'Grow your business, reach more people, and join a stronger local food economy.',
    action: 'Become a partner',
    route: '/restaurants',
    image: restaurantImage,
    alt: 'A local restaurant owner managing customer orders while her team prepares deliveries',
  },
  {
    eyebrow: 'For Riders',
    title: 'Ride for a brighter tomorrow.',
    text: 'Find flexible opportunities and help connect local restaurants with their communities.',
    action: 'Explore rider opportunities',
    route: '/riders',
    image: riderImage,
    alt: 'A delivery rider ready for work in Johannesburg',
  },
];

function AudienceSection() {
  const navigate = useNavigate();

  return (
    <section className={css(styles.section)}>
      <header className={css(styles.header)}>
        <p className={css(styles.eyebrow)}>Made for the whole community</p>
        <h2 className={css(styles.heading)}>People, food, opportunity.</h2>
      </header>

      <div className={css(styles.list)}>
        {audiences.map((item, index) => (
          <article className={css(styles.story, index % 2 === 1 && styles.storyReverse)} key={item.eyebrow}>
            <div className={css(styles.imageWrap)}>
              <img className={css(styles.image)} src={item.image} alt={item.alt} />
            </div>
            <div className={css(styles.content)}>
              <p className={css(styles.eyebrow)}>{item.eyebrow}</p>
              <h3 className={css(styles.title)}>{item.title}</h3>
              <p className={css(styles.text)}>{item.text}</p>
              <button className={css(styles.link)} onClick={() => navigate(item.route)}>
                <span>{item.action}</span>
                <ArrowIcon size={17} />
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

const styles = StyleSheet.create({
  section: {
    ...shared.container,
    paddingTop: '64px',
    paddingBottom: '86px',
    '@media (max-width: 720px)': {
      paddingTop: '44px',
      paddingBottom: '58px',
    },
  },
  header: {
    maxWidth: '760px',
    marginBottom: '52px',
    '@media (max-width: 720px)': {
      marginBottom: '34px',
    },
  },
  eyebrow: shared.eyebrow,
  heading: {
    ...shared.sectionTitle,
    fontSize: 'clamp(2rem, 4vw, 3.5rem)',
    '@media (max-width: 520px)': {
      fontSize: '1.75rem',
    },
  },
  list: {
    display: 'grid',
    gap: '70px',
    '@media (max-width: 720px)': {
      gap: '46px',
    },
  },
  story: {
    display: 'grid',
    gridTemplateColumns: 'minmax(230px, 390px) minmax(0, 1fr)',
    alignItems: 'center',
    gap: 'clamp(34px, 7vw, 92px)',
    maxWidth: '980px',
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
      gap: '22px',
    },
  },
  storyReverse: {
    marginLeft: 'auto',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(230px, 390px)',
    '> div:first-child': { order: 2 },
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
      '> div:first-child': { order: 0 },
    },
  },
  imageWrap: {
    width: '100%',
    maxWidth: '390px',
    aspectRatio: '1 / 1',
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '50%',
    boxSizing: 'border-box',
    backgroundColor: colors.sand,
    '@media (max-width: 720px)': {
      maxWidth: '320px',
      margin: '0 auto',
    },
  },
  image: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },
  content: { maxWidth: '500px' },
  title: {
    margin: '0 0 14px',
    color: colors.ink,
    fontSize: 'clamp(1.7rem, 3vw, 2.65rem)',
    lineHeight: 1.08,
    '@media (max-width: 520px)': {
      fontSize: '1.5rem',
    },
  },
  text: {
    margin: '0 0 20px',
    color: colors.muted,
    fontSize: '1.04rem',
    lineHeight: 1.7,
    '@media (max-width: 520px)': {
      fontSize: '0.98rem',
    },
  },
  link: {
    padding: '0 0 7px',
    border: 0,
    borderBottom: `1px solid ${colors.gold}`,
    backgroundColor: 'transparent',
    color: colors.ink,
    fontSize: '0.96rem',
    fontWeight: '900',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
});

export default AudienceSection;
